import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom"; // react-router-dom
import {
    FiUser,
    FiMail,
    FiCalendar,
    FiHash,
    FiPhone,
    FiBriefcase,
    FiCheckCircle,
    FiArrowRight,
    FiInfo,
} from "react-icons/fi";
import { useRoForm } from "./RoContext.js";

const steps = [
    { label: "Personal Info", link: "/personalinformationmanagerhub" },
    { label: "Residency Address", link: "/residencyaddmanager" },
    { label: "KYC", link: "/kycmanagerhub" },
    { label: "Bank Details", link: "/bankaccountdetailsmanagerhub" },
    { label: "Agreement", link: "/aggrementdetailsmanager" },
];

const validationSchema = Yup.object({
    email: Yup.string().email("Invalid email").required("Email is required"),
    fullName: Yup.string().required("Full name is required"),
    dob: Yup.date()
        .nullable()
        .required("Date of birth is required")
        .max(new Date(), "DOB can't be in the future"),
    age: Yup.number()
        .typeError("Age must be a number")
        .min(1, "Enter a valid age")
        .max(120, "Enter a valid age")
        .required("Age is required"),
    gender: Yup.string().required("Gender is required"),
    contactNo: Yup.string()
        .matches(/^[0-9]{10}$/, "Must be 10 digits")
        .required("Contact number is required"),
    education: Yup.string().required("Education is required"),
    currentWork: Yup.string().required("Current work is required"),
    percentage: Yup.number()
        .typeError("Please select a percentage")
        .min(0)
        .max(100)
        .required("Please select a percentage"),
    alternateContactNo: Yup.string()
        .matches(/^[0-9]{10}$/, "Must be 10 digits")
        .notRequired()
        .nullable()
        .transform((v) => (v === "" ? null : v)),
});

const PersonalInformationManagerHUB = ({ currentStep = 1 }) => {
    const { formData, updateFormData, completedSteps, setCompletedSteps } = useRoForm();
    const navigate = useNavigate();

    // convert session dob (if numeric) to yyyy-mm-dd; otherwise leave blank
    const formatDob = (dobVal) => {
        if (!dobVal) return "";
        if (typeof dobVal === "number" || /^\d+$/.test(String(dobVal))) {
            try {
                const d = new Date(parseInt(dobVal));
                return d.toISOString().split("T")[0];
            } catch {
                return "";
            }
        }
        // if already string date
        return dobVal.split("T")[0] || dobVal;
    };

    const blankForm = {
        email: "",
        fullName: "",
        dob: "",
        age: "",
        gender: "",
        contactNo: "",
        education: "",
        currentWork: "",
        percentage: "",
        alternateContactNo: "",
    };

    const initialValues = { ...blankForm, ...formData };

    const handleSubmit = (values, { setSubmitting }) => {

        if (!completedSteps.includes('/personalinformationmanagerhub')) {
            setCompletedSteps([...completedSteps, '/personalinformationmanagerhub']);
        }
        updateFormData(values, '/personalinformationmanagerhub'); // e.g., '/kycverification'


        // navigate to next step in wizard (example)
        navigate("/residencyaddmanager");
        // Replace navigate with your API call to save data
    };

    return (
        <div className="px-4 py-6 bg-white rounded-lg mb-10">
            <h1 className="font-bold text-gray-800 mb-7">Step {currentStep}: Personal Information</h1>

            {/* Steps bar - horizontally scrollable */}
            <div className="flex justify-between pb-6 w-full overflow-x-auto whitespace-nowrap cursor-pointer items-center relative mb-4">
                {steps.map((step, index) => (
                    <div key={index} className="flex-1 text-white flex flex-col items-center flex-shrink-0 min-w-[140px] relative">
                        {index !== 0 && <div className="absolute top-5 left-[-50%] w-[100%] h-0.5 bg-gray-300 z-0" />}
                        <div
                            className="z-10"
                            onClick={() => navigate(step.link)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={() => { }}
                        >
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${index + 1 === currentStep
                                    ? "bg-slate-900 text-white shadow-md"
                                    : "bg-white text-gray-500 border border-gray-300"
                                    }`}
                            >
                                {index + 1}
                            </div>
                        </div>
                        <span className="mt-2 text-sm text-center text-gray-700">{step.label}</span>
                    </div>
                ))}
            </div>

            <div className="bg-white md:border md:p-6 rounded-lg">
                <h2 className="text-lg font-bold mb-6 text-black">Personal Information</h2>

                <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ errors, touched, isSubmitting }) => (
                        <Form className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {/* Full Name */}
                            <div>
                                <label htmlFor="fullName" className="block text-sm font-medium text-slate-700 mb-2">
                                    <div className="flex items-center gap-2">
                                        <FiUser /> Full Name <span className="text-red-600">*</span>
                                    </div>
                                </label>
                                <Field
                                    id="fullName"
                                    name="fullName"
                                    type="text"
                                    placeholder="Enter your full name"
                                    className={`w-full px-4 py-2 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-900 border ${errors.fullName && touched.fullName ? "border-red-500" : "border-gray-300"
                                        } bg-white`}
                                />
                                <ErrorMessage name="fullName" component="div" className="text-red-500 text-xs mt-1" />
                            </div>

                            {/* Email */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                                    <div className="flex items-center gap-2">
                                        <FiMail /> Email <span className="text-red-600">*</span>
                                    </div>
                                </label>
                                <Field
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder="Enter your email"
                                    // Keep email editable if you want user to change; make readOnly instead of disabled if you prefer
                                    className={`w-full px-4 py-2 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-900 border ${errors.email && touched.email ? "border-red-500" : "border-gray-300"
                                        } bg-white`}
                                />
                                <ErrorMessage name="email" component="div" className="text-red-500 text-xs mt-1" />
                            </div>

                            {/* DOB */}
                            <div>
                                <label htmlFor="dob" className="block text-sm font-medium text-slate-700 mb-2">
                                    <div className="flex items-center gap-2">
                                        <FiCalendar /> Date of Birth <span className="text-red-600">*</span>
                                    </div>
                                </label>
                                <Field
                                    id="dob"
                                    name="dob"
                                    type="date"
                                    className={`w-full px-4 py-2 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-900 border ${errors.dob && touched.dob ? "border-red-500" : "border-gray-300"
                                        } bg-white`}
                                />
                                <ErrorMessage name="dob" component="div" className="text-red-500 text-xs mt-1" />
                            </div>

                            {/* Age */}
                            <div>
                                <label htmlFor="age" className="block text-sm font-medium text-slate-700 mb-2">
                                    <div className="flex items-center gap-2">
                                        <FiHash /> Age <span className="text-red-600">*</span>
                                    </div>
                                </label>
                                <Field
                                    id="age"
                                    name="age"
                                    type="number"
                                    placeholder="Enter your age"
                                    className={`w-full px-4 py-2 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-900 border ${errors.age && touched.age ? "border-red-500" : "border-gray-300"
                                        } bg-white`}
                                    onWheel={(e) => e.target.blur()} // stop mouse wheel
                                    onKeyDown={(e) => {
                                        if (["ArrowUp", "ArrowDown"].includes(e.key)) {
                                            e.preventDefault(); // block arrows ↑ ↓
                                        }
                                    }}
                                />
                                <ErrorMessage name="age" component="div" className="text-red-500 text-xs mt-1" />
                            </div>

                            {/* Gender */}
                            <div>
                                <label htmlFor="gender" className="block text-sm font-medium text-slate-700 mb-2">
                                    <div className="flex items-center gap-2">
                                        <FiInfo /> Gender <span className="text-red-600">*</span>
                                    </div>
                                </label>
                                <Field
                                    as="select"
                                    id="gender"
                                    name="gender"
                                    className={`w-full px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 border ${errors.gender && touched.gender ? "border-red-500" : "border-gray-300"
                                        } bg-white`}
                                >
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </Field>
                                <ErrorMessage name="gender" component="div" className="text-red-500 text-xs mt-1" />
                            </div>

                            {/* Contact No */}
                            <div>
                                <label htmlFor="contactNo" className="block text-sm font-medium text-slate-700 mb-2">
                                    <div className="flex items-center gap-2">
                                        <FiPhone /> Contact No. <span className="text-red-600">*</span>
                                    </div>
                                </label>
                                <Field
                                    id="contactNo"
                                    name="contactNo"
                                    type="text"
                                    placeholder="Enter primary contact number"
                                    className={`w-full px-4 py-2 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-900 border ${errors.contactNo && touched.contactNo ? "border-red-500" : "border-gray-300"
                                        } bg-white`}
                                />
                                <ErrorMessage name="contactNo" component="div" className="text-red-500 text-xs mt-1" />
                            </div>

                            {/* Alternate Contact No */}
                            <div>
                                <label htmlFor="alternateContactNo" className="block text-sm font-medium text-slate-700 mb-2">
                                    <div className="flex items-center gap-2">
                                        <FiPhone /> Alternate Contact
                                    </div>
                                </label>
                                <Field
                                    id="alternateContactNo"
                                    name="alternateContactNo"
                                    type="text"
                                    placeholder="Enter alternate contact number (optional)"
                                    className={`w-full px-4 py-2 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-900 border ${errors.alternateContactNo && touched.alternateContactNo ? "border-red-500" : "border-gray-300"
                                        } bg-white`}
                                />
                                <ErrorMessage name="alternateContactNo" component="div" className="text-red-500 text-xs mt-1" />
                            </div>

                            {/* Education */}
                            <div>
                                <label htmlFor="education" className="block text-sm font-medium text-slate-700 mb-2">
                                    <div className="flex items-center gap-2">
                                        <FiBriefcase /> Education <span className="text-red-600">*</span>
                                    </div>
                                </label>
                                <Field
                                    id="education"
                                    name="education"
                                    type="text"
                                    placeholder="Enter your education"
                                    className={`w-full px-4 py-2 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-900 border ${errors.education && touched.education ? "border-red-500" : "border-gray-300"
                                        } bg-white`}
                                />
                                <ErrorMessage name="education" component="div" className="text-red-500 text-xs mt-1" />
                            </div>

                            {/* Current Work */}
                            <div>
                                <label htmlFor="currentWork" className="block text-sm font-medium text-slate-700 mb-2">
                                    <div className="flex items-center gap-2">
                                        <FiBriefcase /> Current Work <span className="text-red-600">*</span>
                                    </div>
                                </label>
                                <Field
                                    id="currentWork"
                                    name="currentWork"
                                    type="text"
                                    placeholder="Enter your current job or business"
                                    className={`w-full px-4 py-2 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-900 border ${errors.currentWork && touched.currentWork ? "border-red-500" : "border-gray-300"
                                        } bg-white`}
                                />
                                <ErrorMessage name="currentWork" component="div" className="text-red-500 text-xs mt-1" />
                            </div>

                            {/* English Language Percentage */}
                            <div>
                                <label htmlFor="percentage" className="block text-sm font-medium text-slate-700 mb-2">
                                    <div className="flex items-center gap-2">
                                        <FiCheckCircle /> English Language (%) <span className="text-red-600">*</span>
                                    </div>
                                </label>
                                <Field
                                    as="select"
                                    id="percentage"
                                    name="percentage"
                                    className={`w-full px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 border ${errors.percentage && touched.percentage ? "border-red-500" : "border-gray-300"
                                        } bg-white`}
                                >
                                    <option value="">Select Percentage</option>
                                    {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((val) => (
                                        <option key={val} value={val}>
                                            {val}%
                                        </option>
                                    ))}
                                </Field>
                                <ErrorMessage name="percentage" component="div" className="text-red-500 text-xs mt-1" />
                            </div>

                            {/* Buttons - full width on small screens */}
                            <div className="sm:col-span-2 md:col-span-3 flex gap-3 mt-2">


                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="ml-auto inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800 transition"
                                >
                                    Next <FiArrowRight />
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default PersonalInformationManagerHUB;

// optional page wrapper if needed
export function Page() {
    return <PersonalInformationManagerHUB currentStep={1} />;
}
